
<template>
  <div class="container-fluid">
    <Toolbar class="m-2">
      <template #start>
        <h2>{{ $t('nav.venue') }}</h2>
      </template>
      <template #end>
        <Button
          :label="$t('venuePage.createVenue')"
          icon="pi pi-plus"
          class=" mr-2 p-button-outlined p-button-rounded"
          @click="toVenueForm()"
        />
      </template>
    </Toolbar>

    <DataTable
      v-model:sortField="venueFilter.pageable.sortField"
      v-model:sortOrder="venueFilter.pageable.sortOrder"
      v-model:filters="venueFilter.filters"
      v-model:rows="venueFilter.pageable.size"
      striped-rows
      row-hover
      lazy
      paginator
      paginator-position="both"
      :value="venues"
      :loading="isLoading"
      :rows="venueFilter.pageable.size"
      :total-records="totalVenuesCount"
      @page="onPageChange($event)"
      @sort="onSort($event)"
      @filter="getVenues"
      responsive-layout="scroll"
      class="p-datatable-sm"
      filter-display="row"
    >
      <Column
        :exportable="false"
        style="width: 175px; min-width: 175px;"
      >
        <template  #body="{ data }">
          <Button
            icon="pi pi-eye"
            class="p-button-rounded p-button-secondary mx-1"
            :title="$t('action.crud.read')"
            @click="toVenueDetail(data.id)"
          />

          <Button
            icon="pi pi-pencil"
            class="p-button-rounded mx-1"
            :title="$t('action.crud.update')"
            @click="toVenueForm(data.id)"
          />

          <Button
            icon="pi pi-trash"
            class="p-button-rounded p-button-danger mx-1"
            :title="$t('action.crud.delete')"
            @click="openDeleteDialog(data.id)"
          />
        </template>
      </Column>
      <Column
        field="code"
        :header="$t('venuePage.code')"
        :sortable="true"
        :show-filter-menu="false"
        class="col-tab col-3"
      >
        <template #body="{ data }">
          {{ data.code }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>
      <Column class="col-tab col-3" field="address" :header="$t('venuePage.address')" :sortable="true" :show-filter-menu="false">
        <template #body="{ data }">
          {{ data.address.name }} {{ data.address.postcode }} {{ data.address.city }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>
      <Column class="col-tab col-3" field="timeZone" :header="$t('venuePage.timeZone')" :sortable="true" :show-filter-menu="false">
        <template #body="{ data }">
          {{ data.timeZone }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>
      <Column
        class="col-1"
        field="active"
        :header="$t('venuePage.active')"
        :sortable="false"
        :show-filter-menu="false"
      >
        <template #filter="{ filterModel, filterCallback }">
          <TriStateCheckbox
            v-model="filterModel.value"
            @change="filterCallback()"
          />
        </template>
        <template #body="{ data }">
          <div class="p-checkbox p-component">
            <Checkbox
              v-model="data.active"
              :binary="true"
              @click.prevent="
                data.active
                  ? openDeactivateDialog(data.id)
                  : openActivateDialog(data.id)
              "
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog
      v-model:visible="dialogDelete"
      :modal="true"
    >
      <template #header>
        <h3>{{ $t('venuePage.dialog.delete.header') }}</h3>
      </template>

      <div class="confirmation-content">
        <span>{{ $t('venuePage.dialog.delete.content') }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('venuePage.dialog.delete.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="dialogDelete = false"
        />
        <Button
          :label="$t('venuePage.dialog.delete.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="deleteVenue"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="dialogActivate"
      :modal="true"
      @after-hide="getVenues"
    >
      <template #header>
        <h3>{{ $t('venuePage.dialog.activate.header') }}</h3>
      </template>


      <div class="confirmation-content">
        <span>{{ $t("venuePage.dialog.activate.content") }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('venuePage.dialog.activate.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="abort"
        />
        <Button
          :label="$t('venuePage.dialog.activate.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="activateVenue"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="dialogDeactivate"
      :modal="true"
      @after-hide="getVenues"
    >
      <template #header>
        <h3>{{ $t('venuePage.dialog.deactivate.header') }}</h3>
      </template>

      <div class="confirmation-content">
        <span>{{ $t("venuePage.dialog.deactivate.content") }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('venuePage.dialog.deactivate.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="abort"
        />
        <Button
          :label="$t('venuePage.dialog.deactivate.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="deactivateVenue"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Button from "primevue/button";
import Toolbar from "primevue/toolbar";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";
import Dialog from "primevue/dialog";
import {reactive} from "vue";
import useVenueFilter from "@/composables/filters/useVenueFilter";
import TriStateCheckbox from "primevue/tristatecheckbox";

export default {
  name: "VenueTable",
  components: {TriStateCheckbox, DataTable, Button, Toolbar, Column, InputText, Checkbox, Dialog },
  setup(){
    const venueFilter = reactive(useVenueFilter());

    return {
      venueFilter: venueFilter,
    }
  },
  data() {
    return {
      /** @type {VenueDto[]}*/
      venues: [],
      totalVenuesCount: 0,
      isLoading: false,
      dialogDelete: false,
      dialogActivate: false,
      dialogDeactivate: false
    }
  },
  mounted() {
    this.getVenues();
  },
  methods: {
    async getVenues() {
      this.venues = [];
      this.isLoading = true;
      try {
        const { content, totalElements } = await this.$venueService.fetchVenuesPagingAndSorting(this.venueFilter.getQueryParams());
        this.venues = content;
        this.totalVenuesCount = totalElements;
      } catch (e) {
        console.error(e);
        this.$emitter.emit('toast', { severity: 'error', message: 'error.global' });
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange(event) {
      this.venueFilter.pageable.page = event.page;
      this.getVenues();
    },
    onSort(event) {
      this.venueFilter.pageable.sortField = event.sortField;
      this.venueFilter.pageable.sortOrder = event.sortOrder;
      this.getVenues();
    },
    toVenueDetail(venueId) {
      this.$router.push({name: "venueDetail", params: {id: venueId}});
    },
    toVenueForm(venueId) {
      if (venueId) {
        this.$router.push({name: "venueUpdate", params: {id: venueId}});
      } else {
        this.$router.push({name: "venueCreate"});
      }
    },
    openDeleteDialog(id) {
      this.selectedId = id;
      this.dialogDelete = true;
    },
    openActivateDialog(id) {
      this.selectedId = id;
      this.dialogActivate = true;
    },
    openDeactivateDialog(id) {
      this.selectedId = id;
      this.dialogDeactivate = true;
    },
    async deleteVenue() {
      this.dialogDelete = false;
      try {
        const del = await this.$venueService.deleteVenue(this.selectedId);
        if (del) {
          this.$emitter.emit("toast", {
            severity: "success",
            message: "venuePage.toast.delete.success",
          });
        } else {
          this.$emitter.emit("toast", {
            severity: "error",
            message: "venuePage.toast.delete.error",
          });
        }
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error)).join(', ')
        this.$emitter.emit("toast", {
          severity: "error",
          message: errors,
        });
      }
      this.getVenues();
    },
    abort() {
      this.dialogActivate = false;
      this.dialogDeactivate = false;
      this.getVenues();
    },
    async activateVenue() {
      this.dialogActivate = false;
      const activate = await this.$venueService.activateVenue(
        this.selectedId
      );
      if (activate) {
        this.$emitter.emit("toast", {
          severity: "success",
          message: "venuePage.toast.activate.success",
        });
      } else {
        this.$emitter.emit("toast", {
          severity: "error",
          message: "venuePage.toast.activate.error",
        });
      }
      this.getVenues();
    },
    async deactivateVenue() {
      this.dialogDeactivate = false;
      const deactivate = await this.$venueService.deactivateVenue(this.selectedId);
      if (deactivate) {
        this.$emitter.emit("toast", {
          severity: "success",
          message: "venuePage.toast.deactivate.success",
        });
      } else {
        this.$emitter.emit("toast", {
          severity: "error",
          message: "venuePage.toast.deactivate.error",
        });
      }
      this.getVenues();
    },
  }
}
</script>

<style scoped lang="scss">
.pi-check {
  color: white;
}
</style>
